import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ChatConfig, ConversationHistory, ChatMessage, SavedConversation } from './types';
import { ChatInterface } from './components/ChatInterface';
import { AdminPanel } from './components/AdminPanel';
import { Login } from './components/Login';
import { ChatGPTService } from './services/chatgpt';
import { ConversationService } from './services/conversationService';
import { AuthService } from './services/authService';
import { systemPromptText, backendSystemPromptText, labelPromptsText } from './prompts/PromptText';
import logoJudicia from './images/judicia-logo.png';

const defaultConfig: ChatConfig = {
  systemPrompt: systemPromptText,
  backendSystemPrompt: backendSystemPromptText,
  temperature: 0.7,
  maxTokens: 1000,
  model: 'gpt-4o-mini', // ✅ Modèle accessible à toutes les clés API
  contextBefore: '',
  contextAfter: '',
  labelPrompts: labelPromptsText,
};

// Message de bienvenue initial
const welcomeMessage: ChatMessage = {
  role: 'assistant',
  content: "Bonjour ! Je suis votre assistant legal IA. " +
           "Je peux vous assister sur des questions juridiques, fournir des liens vers des documents légaux, ou encore vous mettre en relation avec un avocat. " +
           "Comment puis-je vous aider aujourd'hui ?",
  timestamp: new Date(),
};

// Fonction pour charger la configuration depuis le localStorage
const loadConfigFromStorage = (): ChatConfig => {
  try {
    const savedConfig = localStorage.getItem('judicia-config');
    if (savedConfig) {
      const parsedConfig = JSON.parse(savedConfig);
      return {
        ...defaultConfig,
        ...parsedConfig,
        // ⚠️ FORCE le modèle à gpt-4o-mini (override localStorage qui peut avoir gpt-4)
        model: 'gpt-4o-mini'
      };
    }
  } catch (error) {
    console.error('Erreur lors du chargement de la configuration:', error);
  }
  return defaultConfig;
};

// Fonction pour sauvegarder la configuration dans le localStorage
const saveConfigToStorage = (config: ChatConfig) => {
  try {
    localStorage.setItem('judicia-config', JSON.stringify(config));
  } catch (error) {
    console.error('Erreur lors de la sauvegarde de la configuration:', error);
  }
};

// Fonction pour charger les conversations sauvegardées
const loadSavedConversations = (): SavedConversation[] => {
  try {
    const saved = localStorage.getItem('judicia-conversations');
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.map((conv: any) => ({
        ...conv,
        createdAt: new Date(conv.createdAt),
        lastModified: new Date(conv.lastModified)
      }));
    }
  } catch (error) {
    console.error('Erreur lors du chargement des conversations:', error);
  }
  return [];
};

// Fonction pour sauvegarder les conversations
const saveConversationsToStorage = (conversations: SavedConversation[]) => {
  try {
    localStorage.setItem('judicia-conversations', JSON.stringify(conversations));
  } catch (error) {
    console.error('Erreur lors de la sauvegarde des conversations:', error);
  }
};

// Fonction pour générer un titre de conversation basé sur le premier message utilisateur
const generateConversationTitle = (messages: ChatMessage[]): string => {
  const firstUserMessage = messages.find(msg => msg.role === 'user');
  if (firstUserMessage) {
    const content = firstUserMessage.content;
    return content.length > 50 ? content.substring(0, 50) + '...' : content;
  }
  return 'Nouvelle conversation';
};

// Composant principal de l'application
const AppContent: React.FC = () => {
  const [config, setConfig] = useState<ChatConfig>(loadConfigFromStorage);
  const [conversationHistory, setConversationHistory] = useState<ConversationHistory>({
    messages: [welcomeMessage],
    totalTokens: 0,
    detectedLabels: []
  });
  const [savedConversations, setSavedConversations] = useState<SavedConversation[]>(loadSavedConversations);
  const [viewingConversation, setViewingConversation] = useState<SavedConversation | null>(null);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState<boolean>(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState<boolean>(true);

  // Vérifier l'authentification au chargement
  useEffect(() => {
    const checkAuthentication = async () => {
      const result = await AuthService.checkAuth();
      setIsAdminAuthenticated(result.authenticated);
      setIsCheckingAuth(false);
    };
    
    checkAuthentication();
  }, []);

  // Charger les conversations depuis le serveur MySQL au démarrage
  useEffect(() => {
    const loadServerConversations = async () => {
      try {
        const result = await ConversationService.listConversations();
        if (result.success && result.conversations) {
          setSavedConversations(result.conversations);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des conversations depuis le serveur:', error);
      }
    };
    loadServerConversations();
  }, []);

  // Sauvegarder la configuration quand elle change
  useEffect(() => { saveConfigToStorage(config); }, [config]);
  useEffect(() => { saveConversationsToStorage(savedConversations); }, [savedConversations]);

  // Auto-save conversations when they change - avec sauvegarde MySQL
  useEffect(() => {
    if (conversationHistory.messages.length > 1) {
      const timeoutId = setTimeout(async () => {
        // Chercher une conversation existante
        const existingConversation = savedConversations.find(conv => 
          conv.messages.length === conversationHistory.messages.length &&
          conv.messages.every((msg, index) => 
            msg.content === conversationHistory.messages[index].content &&
            msg.role === conversationHistory.messages[index].role
          )
        );

        if (existingConversation) {
          // Mettre à jour la conversation existante
          const updatedConversation: SavedConversation = {
            ...existingConversation,
            messages: conversationHistory.messages,
            totalTokens: conversationHistory.totalTokens,
            lastModified: new Date()
          };

          setSavedConversations(prev => 
            prev.map(conv => conv.id === existingConversation.id ? updatedConversation : conv)
          );

          // Sauvegarder sur le serveur MySQL
          ConversationService.saveConversation(updatedConversation).catch(console.error);
        } else {
          // Nouvelle conversation
          const newId = Date.now().toString() + '_' + Math.random().toString(36).substr(2, 9);
          
          const newConversation: SavedConversation = {
            id: newId,
            title: generateConversationTitle(conversationHistory.messages),
            messages: conversationHistory.messages,
            totalTokens: conversationHistory.totalTokens,
            createdAt: new Date(),
            lastModified: new Date(),
            summary: 'Analyse en cours...',
            contactInfo: '...'
          };

          setSavedConversations(prev => [newConversation, ...prev]);
          
          // Sauvegarder immédiatement sur MySQL
          ConversationService.saveConversation(newConversation).catch(console.error);

          // Générer le résumé de manière asynchrone
          ChatGPTService.generateConversationSummary(conversationHistory.messages)
            .then(({ summary, contactInfo }) => {
              const updatedConv = { ...newConversation, summary, contactInfo };
              setSavedConversations(prev => 
                prev.map(conv => conv.id === newId ? updatedConv : conv)
              );
              // Mettre à jour sur MySQL avec le résumé
              ConversationService.saveConversation(updatedConv).catch(console.error);
            })
            .catch(console.error);
        }
      }, 2000); // Délai de 2 secondes

      return () => clearTimeout(timeoutId);
    }
  }, [conversationHistory.messages.length]); // Dépendance sur la longueur uniquement

  const clearHistory = () => {
    setConversationHistory({
      messages: [welcomeMessage], // Garder le message de bienvenue même après effacement
      totalTokens: 0,
      detectedLabels: []
    });
    setViewingConversation(null);
  };

  const loadConversation = async (conversation: SavedConversation | null) => {
    if (!conversation) {
      setViewingConversation(null);
      return;
    }

    // Si la conversation n'a pas de messages (juste métadonnées), charger depuis MySQL
    if (!conversation.messages || conversation.messages.length === 0) {
      const result = await ConversationService.getConversation(conversation.id);
      if (result.success && result.conversation) {
        setViewingConversation(result.conversation);
      } else {
        setViewingConversation(conversation);
      }
    } else {
      setViewingConversation(conversation);
    }
  };

  const deleteConversation = async (id: string) => {
    // Supprimer de la base de données MySQL
    await ConversationService.deleteConversation(id);
    
    // Supprimer de l'état local
    setSavedConversations(prev => prev.filter(conv => conv.id !== id));
    if (viewingConversation?.id === id) {
      setViewingConversation(null);
    }
  };

  const updateConversationTitle = (id: string, newTitle: string) => {
    setSavedConversations(prev => 
      prev.map(conv => 
        conv.id === id 
          ? { ...conv, title: newTitle, lastModified: new Date() }
          : conv
      )
    );
    if (viewingConversation?.id === id) {
      setViewingConversation(prev => prev ? { ...prev, title: newTitle, lastModified: new Date() } : null);
    }
  };

  const updateConversationSummary = async (id: string) => {
    const conversation = savedConversations.find(conv => conv.id === id);
    if (conversation) {
      try {
        const { summary, contactInfo } = await ChatGPTService.generateConversationSummary(conversation.messages);
        
        setSavedConversations(prev => 
          prev.map(conv => 
            conv.id === id 
              ? { ...conv, summary, contactInfo, lastModified: new Date() }
              : conv
          )
        );
        
        if (viewingConversation?.id === id) {
          setViewingConversation(prev => prev ? { ...prev, summary, contactInfo, lastModified: new Date() } : null);
        }
      } catch (error) {
        console.error('Erreur lors de la mise à jour du résumé:', error);
      }
    }
  };

  // Fonction de connexion admin (callback après succès)
  const handleAdminLoginSuccess = () => {
    setIsAdminAuthenticated(true);
  };

  // Fonction de déconnexion
  const handleAdminLogout = async () => {
    await AuthService.logout();
    setIsAdminAuthenticated(false);
  };

  
  return (
    <div className="container">
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        padding: '12px 0',
        marginBottom: '16px',
      }}
    >
      <img
        src={logoJudicia}
        alt="Logo JudicIA"
        style={{ width: '90px', height: 'auto' }} // logo légèrement agrandi
      />
      <div style={{ textAlign: 'left' }}>
        <h1 style={{ color: 'white', fontSize: '2rem', margin: 0 }}>
          JudicIA - Assistant Legal IA
        </h1>
        <p style={{ color: 'white', opacity: 0.9, margin: 0, fontSize: '0.9rem' }}>
          Spécialisé en droit belge, européen et international
        </p>
      </div>
    </header>


      <div className="tab-content">
        <Routes>
          <Route 
            path="/" 
            element={
              <ChatInterface 
                config={config} 
                conversationHistory={conversationHistory}
                setConversationHistory={setConversationHistory}
                onClearHistory={clearHistory}
              />
            } 
          />
          <Route 
            path="/admin-login" 
            element={
              isCheckingAuth ? (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  minHeight: '100vh',
                  fontSize: '1.5rem',
                  color: '#7f8c8d'
                }}>
                  <span className="loading"></span>
                  Vérification...
                </div>
              ) : isAdminAuthenticated ? (
                <Navigate to="/admin" replace />
              ) : (
                <Login onLoginSuccess={handleAdminLoginSuccess} />
              )
            } 
          />
          <Route 
            path="/admin" 
            element={
              isCheckingAuth ? (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  minHeight: '100vh',
                  fontSize: '1.5rem',
                  color: '#7f8c8d'
                }}>
                  <span className="loading"></span>
                  Vérification...
                </div>
              ) : isAdminAuthenticated ? (
                <div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    padding: '10px 20px',
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '10px',
                    marginBottom: '20px'
                  }}>
                    <button
                      onClick={handleAdminLogout}
                      style={{
                        padding: '8px 16px',
                        background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <span>🚪</span>
                      Se déconnecter
                    </button>
                  </div>
                  <AdminPanel 
                    config={config} 
                    onConfigChange={setConfig} 
                    onClearHistory={clearHistory}
                    savedConversations={savedConversations}
                    onLoadConversation={loadConversation}
                    onDeleteConversation={deleteConversation}
                    onUpdateConversationTitle={updateConversationTitle}
                    onUpdateConversationSummary={updateConversationSummary}
                    viewingConversation={viewingConversation}
                    onRefreshConversations={async () => {
                      const result = await ConversationService.listConversations();
                      if (result.success && result.conversations) {
                        setSavedConversations(result.conversations);
                      }
                    }}
                  />
                </div>
              ) : (
                <Navigate to="/admin-login" replace />
              )
            } 
          />
        </Routes>
      </div>
    </div>
  );
};

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App; 